import { useState, useEffect, FormEvent } from "react";
import { useAuth } from "./AuthContext";
import {
  User,
  getUsers,
  createUser,
  updateUserPassword,
  deleteUser,
} from "./api";
import "./Admin.css";

interface AdminProps {
  onBack: () => void;
}

export default function Admin({ onBack }: AdminProps) {
  const { user: currentUser, logout } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Create user form
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newIsAdmin, setNewIsAdmin] = useState(false);
  const [creating, setCreating] = useState(false);

  // Password change state
  const [changingPasswordId, setChangingPasswordId] = useState<number | null>(null);
  const [newPasswordValue, setNewPasswordValue] = useState("");

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const usersList = await getUsers();
      setUsers(usersList);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setCreating(true);

    try {
      await createUser(newUsername, newPassword, newIsAdmin);
      setNewUsername("");
      setNewPassword("");
      setNewIsAdmin(false);
      await loadUsers();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleChangePassword = async (userId: number) => {
    if (!newPasswordValue) return;

    try {
      await updateUserPassword(userId, newPasswordValue);
      setChangingPasswordId(null);
      setNewPasswordValue("");
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteUser = async (userId: number, username: string) => {
    if (!confirm(`Delete user "${username}"?`)) return;

    try {
      await deleteUser(userId);
      await loadUsers();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  if (loading) {
    return <div className="admin-page">Loading...</div>;
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <div className="admin-header-actions">
          <button onClick={onBack}>Back</button>
          <button onClick={handleLogout}>Logout</button>
        </div>
      </div>

      {error && <div className="admin-error">{error}</div>}

      <div className="admin-section">
        <h2>Create User</h2>
        <form onSubmit={handleCreateUser} className="create-user-form">
          <input
            type="text"
            placeholder="Username"
            value={newUsername}
            onChange={(e) => setNewUsername(e.target.value)}
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={newIsAdmin}
              onChange={(e) => setNewIsAdmin(e.target.checked)}
            />
            Admin
          </label>
          <button type="submit" disabled={creating}>
            {creating ? "Creating..." : "Create"}
          </button>
        </form>
      </div>

      <div className="admin-section">
        <h2>Users</h2>
        <div className="users-list">
          {users.map((user) => (
            <div key={user.id} className="user-item">
              <div className="user-info">
                <span className="username">
                  {user.username}
                  {user.is_admin === 1 && <span className="admin-badge">admin</span>}
                </span>
                <span className="created">
                  Created: {new Date(user.created_at).toLocaleDateString()}
                </span>
              </div>
              <div className="user-actions">
                {changingPasswordId === user.id ? (
                  <div className="password-change">
                    <input
                      type="password"
                      placeholder="New password"
                      value={newPasswordValue}
                      onChange={(e) => setNewPasswordValue(e.target.value)}
                      autoFocus
                    />
                    <button onClick={() => handleChangePassword(user.id)}>
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setChangingPasswordId(null);
                        setNewPasswordValue("");
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <>
                    <button onClick={() => setChangingPasswordId(user.id)}>
                      Change Password
                    </button>
                    {currentUser?.id !== user.id && (
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteUser(user.id, user.username)}
                      >
                        Delete
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
